import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Tabs, message, Image, Input, Space } from "antd";
import { 
    PlusOutlined, 
    DeleteOutlined, 
    ArrowUpOutlined, 
    ArrowDownOutlined,
    SearchOutlined
} from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../constants/Constants";

const { TabPane } = Tabs;

const HomeProducts = () => {
  const [activeTab, setActiveTab] = useState("1"); // 1 = New Arrivals, 2 = Popular
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- ADD MODAL STATE ---
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchActiveList();
  }, [activeTab]);

  // 1. Fetch the Active List based on Tab
  const fetchActiveList = async () => {
    setLoading(true);
    try {
        const endpoint = activeTab === "1" ? "/products/new-arrivals" : "/products/popular";
        const res = await axios.get(`${BASE_URL}${endpoint}`);
        setListData(res.data || []);
    } catch (error) {
        console.error("Fetch error:", error);
    } finally {
        setLoading(false);
    }
  };

  // 2. Open "Add Product" Modal (Fetch non-active items)
  const openAddModal = async () => {
      setIsAddModalVisible(true);
      try {
          const res = await axios.get(`${BASE_URL}/products/allProductList`);
          setAllProducts(res.data || []);
      } catch (err) {
          message.error("Could not load product list");
      }
  };

  // 3. Logic: Add Product to List
  const handleAddProduct = async (product) => {
      try {
          let payload = {};
          
          if (activeTab === "1") {
              // New Arrival: Append to bottom (Max Order + 1)
              const maxOrder = listData.length > 0 ? Math.max(...listData.map(p => p.newArrivalOrder || 0)) : 0;
              payload = { 
                  newArrival: true, 
                  newArrivalOrder: maxOrder + 1 
              };
          } else {
              // Popular: Append to bottom (Lowest Priority - 1)
              // Since Popular is DESC sorting, "Bottom" means smaller number.
              const minPriority = listData.length > 0 ? Math.min(...listData.map(p => p.priority || 0)) : 0;
              payload = { 
                  isPopular: true, 
                  priority: minPriority - 1 
              };
          }

          await axios.put(`${BASE_URL}/products/update/${product.productId}`, {
              ...product, 
              ...payload  
          });

          message.success("Added to Home Page");
          fetchActiveList(); 
      } catch (err) {
          message.error("Failed to add product");
      }
  };

  // 4. Logic: Remove Product from List
  const handleRemove = async (product) => {
      try {
          const payload = activeTab === "1" 
              ? { newArrival: false, newArrivalOrder: 0 } 
              : { isPopular: false, priority: 0 };

          await axios.put(`${BASE_URL}/products/update/${product.productId}`, {
              ...product,
              ...payload
          });
          
          message.success("Removed from list");
          fetchActiveList();
      } catch (err) {
          message.error("Remove failed");
      }
  };

  // 5. Logic: Move Up / Down (FIXED FOR POPULAR PRODUCTS)
  const moveRow = async (index, direction) => {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= listData.length) return; 

      const itemCurrent = listData[index]; // The item we clicked
      const itemNeighbor = listData[newIndex]; // The item we are swapping with

      try {
          if (activeTab === "1") {
              // --- NEW ARRIVALS (Sort: ASC) ---
              // Simply swap order numbers
              const orderCurrent = itemCurrent.newArrivalOrder || 0;
              const orderNeighbor = itemNeighbor.newArrivalOrder || 0;

              // If equal, force a difference so they actually swap
              if (orderCurrent === orderNeighbor) {
                  await axios.put(`${BASE_URL}/products/update/${itemCurrent.productId}`, { ...itemCurrent, newArrivalOrder: orderNeighbor - 1 });
              } else {
                  await axios.put(`${BASE_URL}/products/update/${itemCurrent.productId}`, { ...itemCurrent, newArrivalOrder: orderNeighbor });
                  await axios.put(`${BASE_URL}/products/update/${itemNeighbor.productId}`, { ...itemNeighbor, newArrivalOrder: orderCurrent });
              }

          } else {
              // --- POPULAR PRODUCTS (Sort: DESC) ---
              // Higher Number = Higher Position
              let prioCurrent = itemCurrent.priority || 0;
              let prioNeighbor = itemNeighbor.priority || 0;

              // FIX: If both are 0 (or equal), swapping 0 with 0 does nothing.
              // We must force the one moving UP to have a HIGHER value.
              if (prioCurrent === prioNeighbor) {
                  if (direction === 'up') {
                      // Moving Up -> Needs higher priority
                      prioCurrent = prioNeighbor + 1;
                  } else {
                      // Moving Down -> Neighbor needs higher priority (effectively pushing Current down)
                      prioNeighbor = prioCurrent + 1;
                  }
                  
                  // Update with new forced values
                  await axios.put(`${BASE_URL}/products/update/${itemCurrent.productId}`, { ...itemCurrent, priority: prioCurrent });
                  await axios.put(`${BASE_URL}/products/update/${itemNeighbor.productId}`, { ...itemNeighbor, priority: prioNeighbor });
              } else {
                  // Standard Swap
                  await axios.put(`${BASE_URL}/products/update/${itemCurrent.productId}`, { ...itemCurrent, priority: prioNeighbor });
                  await axios.put(`${BASE_URL}/products/update/${itemNeighbor.productId}`, { ...itemNeighbor, priority: prioCurrent });
              }
          }

          // Optimistic UI Update to make it feel instant
          const newList = [...listData];
          [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
          setListData(newList);
          
          // Background refresh to sync real DB state
          setTimeout(() => fetchActiveList(), 500);

      } catch (err) {
          message.error("Reorder failed");
      }
  };

  // --- COLUMNS ---
  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (text, record, index) => <span className="text-gray-400">{index + 1}</span>
    },
    {
      title: "Product",
      key: "product",
      render: (_, r) => (
        <div className="flex items-center gap-3">
            <Image 
                width={40} 
                src={r.productPhotos?.[0]?.photoName || "https://via.placeholder.com/40"} 
                className="rounded"
                preview={false}
            />
            <div>
                <div className="font-medium">{r.productName}</div>
                <div className="text-xs text-gray-400">₹{r.productPrice}</div>
            </div>
        </div>
      )
    },
    {
        title: "Reorder",
        key: "order",
        width: 100,
        render: (_, r, index) => (
            <Space>
                <Button 
                    size="small" 
                    icon={<ArrowUpOutlined />} 
                    disabled={index === 0}
                    onClick={() => moveRow(index, 'up')}
                />
                <Button 
                    size="small" 
                    icon={<ArrowDownOutlined />} 
                    disabled={index === listData.length - 1}
                    onClick={() => moveRow(index, 'down')}
                />
            </Space>
        )
    },
    {
        title: "Action",
        key: "action",
        width: 100,
        render: (_, r) => (
            <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleRemove(r)}>
                Remove
            </Button>
        )
    }
  ];

  // --- COLUMNS FOR ADD MODAL ---
  const addColumns = [
      {
          title: "Product",
          dataIndex: "productName",
          key: "name",
      },
      {
          title: "Action",
          key: "add",
          render: (_, r) => {
              const exists = listData.find(item => item.productId === r.productId);
              return (
                  <Button 
                    type={exists ? "default" : "primary"} 
                    disabled={!!exists}
                    size="small"
                    onClick={() => handleAddProduct(r)}
                  >
                      {exists ? "Added" : "Add"}
                  </Button>
              )
          }
      }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
            <h2 className="text-2xl font-bold">Home Page Manager</h2>
            <p className="text-gray-500">Add and reorder products for the home page sections.</p>
        </div>
      </div>

      <div className="bg-white p-4 rounded shadow-sm border">
        {/* REMOVED ICONS FROM TABS */}
        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" tabBarExtraContent={
            <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>Add Product</Button>
        }>
            <TabPane tab="New Arrivals" key="1">
                <Table 
                    dataSource={listData} 
                    columns={columns} 
                    rowKey="productId" 
                    pagination={false} 
                    loading={loading}
                />
            </TabPane>
            <TabPane tab="Popular Products" key="2">
                <Table 
                    dataSource={listData} 
                    columns={columns} 
                    rowKey="productId" 
                    pagination={false}
                    loading={loading}
                />
            </TabPane>
        </Tabs>
      </div>

      {/* --- ADD PRODUCT MODAL --- */}
      <Modal
        title="Add Product to Home Page"
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        footer={null}
        width={600}
      >
          <Input 
            prefix={<SearchOutlined />} 
            placeholder="Search products..." 
            className="mb-4"
            onChange={(e) => setSearchText(e.target.value.toLowerCase())}
          />
          <Table 
            dataSource={allProducts.filter(p => p.productName?.toLowerCase().includes(searchText))}
            columns={addColumns}
            rowKey="productId"
            pagination={{ pageSize: 5 }}
            size="small"
          />
      </Modal>
    </div>
  );
};

export default HomeProducts;